import { useState, useEffect, useRef } from "react";

const rnd = (a, b) => a + Math.random() * (b - a);
const rndInt = (a, b) => Math.floor(rnd(a, b));

/* ══════════════════════════════════════════════════════
   SHARED UI
══════════════════════════════════════════════════════ */
function GameShell({ title, color, score, lives, onBack, children }) {
  return (
    <div style={{
      background: "#04040a", minHeight: "100vh", display: "flex",
      flexDirection: "column", alignItems: "center",
      fontFamily: "'Courier New', monospace", padding: "12px 8px 28px",
      overscrollBehavior: "none",
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", maxWidth: 720, marginBottom: 10, padding: "0 4px" }}>
        <button onClick={onBack} style={{ background: "none", border: "1px solid #2a2a40", color: "#666", padding: "7px 12px", borderRadius: 6, cursor: "pointer", fontFamily: "inherit", fontSize: 11, letterSpacing: "0.1em", WebkitTapHighlightColor: "transparent" }}>← MENU</button>
        <div style={{ color, fontWeight: 700, letterSpacing: "0.2em", fontSize: 14 }}>{title}</div>
        <div style={{ textAlign: "right", minWidth: 64 }}>
          <div style={{ fontSize: 10, color: "#333", letterSpacing: "0.1em" }}>SCORE</div>
          <div style={{ fontSize: 20, color: "#fff", fontWeight: 700, lineHeight: 1.1 }}>{score}</div>
          {lives !== undefined && <div style={{ color, fontSize: 12, marginTop: 2 }}>{"♥ ".repeat(lives).trim()}</div>}
        </div>
      </div>
      <div style={{ position: "relative", width: "100%", maxWidth: 720, display: "flex", flexDirection: "column", alignItems: "center" }}>
        {children}
      </div>
    </div>
  );
}

function Overlay({ color, title, score, onStart, hint }) {
  return (
    <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "rgba(4,4,10,0.92)", borderRadius: 10, zIndex: 10 }}>
      <div style={{ fontSize: "clamp(20px,5vw,36px)", color, fontWeight: 700, letterSpacing: "0.18em", marginBottom: 6, textShadow: `0 0 30px ${color}80` }}>{title}</div>
      {score != null && (
        <div style={{ marginBottom: 20, textAlign: "center" }}>
          <div style={{ fontSize: 11, color: "#444", letterSpacing: "0.15em" }}>FINAL SCORE</div>
          <div style={{ fontSize: 44, color: "#fff", fontWeight: 700, lineHeight: 1 }}>{score}</div>
        </div>
      )}
      {hint && <div style={{ fontSize: 11, color: "#555", marginBottom: 20, letterSpacing: "0.06em", textAlign: "center", padding: "0 20px" }}>{hint}</div>}
      <button onClick={onStart} style={{ background: "transparent", border: `2px solid ${color}`, color, padding: "12px 32px", borderRadius: 8, cursor: "pointer", fontFamily: "'Courier New', monospace", fontSize: 15, fontWeight: 700, letterSpacing: "0.18em", boxShadow: `0 0 24px ${color}33`, WebkitTapHighlightColor: "transparent" }}>{score != null ? "PLAY AGAIN" : "START GAME"}</button>
    </div>
  );
}

function VBtn({ label, color, onDown, onUp, size = 58, style: s = {} }) {
  const [pressed, setPressed] = useState(false);
  const down = () => { setPressed(true); onDown?.(); };
  const up = () => { setPressed(false); onUp?.(); };
  return (
    <button onTouchStart={e => { e.preventDefault(); down(); }} onTouchEnd={e => { e.preventDefault(); up(); }} onTouchCancel={e => { e.preventDefault(); up(); }} onMouseDown={down} onMouseUp={up} onMouseLeave={up}
      style={{ background: pressed ? `${color}28` : `${color}10`, border: `2px solid ${pressed ? color : color + "45"}`, color: pressed ? color : color + "99", borderRadius: 12, width: size, height: size, fontSize: size > 50 ? 22 : 16, cursor: "pointer", userSelect: "none", WebkitUserSelect: "none", touchAction: "none", WebkitTapHighlightColor: "transparent", display: "flex", alignItems: "center", justifyContent: "center", transition: "background 0.08s, border-color 0.08s", boxShadow: pressed ? `0 0 14px ${color}44` : "none", fontFamily: "monospace", ...s }}
    >{label}</button>
  );
}

function DPad({ color, onDir }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 58px)", gridTemplateRows: "repeat(3, 58px)", gap: 5, margin: "12px auto 0" }}>
      <div/><VBtn label="▲" color={color} onDown={() => onDir(0, -1)}/><div/>
      <VBtn label="◄" color={color} onDown={() => onDir(-1, 0)}/>
      <div style={{ width: 58, height: 58, background: `${color}05`, border: `1px solid ${color}20`, borderRadius: 10 }}/>
      <VBtn label="►" color={color} onDown={() => onDir(1, 0)}/>
      <div/><VBtn label="▼" color={color} onDown={() => onDir(0, 1)}/><div/>
    </div>
  );
}

/* ══════════════════════════════════════════════════════
   MENU
══════════════════════════════════════════════════════ */
function Menu({ onSelect }) {
  const games = [
    { id: "snake",     label: "SLITHER",       color: "#00ff88", desc: "Eat. Grow. Survive.",      keys: "Swipe / D-Pad / WASD",         icon: "◈" },
    { id: "asteroids", label: "ROCK BLASTER",   color: "#4488ff", desc: "Shoot. Dodge. Destroy.",   keys: "On-screen buttons / Arrows+Space", icon: "✦" },
    { id: "flappy",    label: "FLUTTER", color: "#ffaa00", desc: "Tap. Fly. Don't die.",     keys: "Tap / Space",                  icon: "◉" },
    { id: "gobbler",   label: "NEON MAZE",   color: "#00ffcc", desc: "Munch. Dodge. Survive.",   keys: "Swipe / D-Pad / WASD",         icon: "⬡" },
  ];
  return (
    <div style={{ background: "#04040a", minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", fontFamily: "'Courier New', monospace", padding: "20px 16px", backgroundImage: "radial-gradient(ellipse at 50% 0%, rgba(68,136,255,0.06) 0%, transparent 65%)" }}>
      <div style={{ position: "fixed", inset: 0, pointerEvents: "none", backgroundImage: "repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(0,0,0,0.06) 3px, rgba(0,0,0,0.06) 4px)" }} />
      <div style={{ position: "relative", zIndex: 1, textAlign: "center", width: "100%" }}>
        <div style={{ fontSize: 10, letterSpacing: "0.5em", color: "#333", marginBottom: 10 }}>INSERT COIN</div>
        <h1 style={{ color: "#fff", fontSize: "clamp(32px,10vw,76px)", margin: "0 0 4px", letterSpacing: "0.1em", fontWeight: 700, textShadow: "0 0 60px rgba(255,255,255,0.12)" }}>ARCADE</h1>
        <div style={{ fontSize: 11, letterSpacing: "0.3em", color: "#334", marginBottom: 36 }}>SELECT YOUR GAME</div>
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap", justifyContent: "center", maxWidth: 900, margin: "0 auto" }}>
          {games.map(g => (
            <div key={g.id} onClick={() => onSelect(g.id)} style={{ background: "transparent", border: `1px solid ${g.color}30`, borderRadius: 14, padding: "22px 20px", cursor: "pointer", width: "clamp(130px, 22vw, 190px)", textAlign: "left", WebkitTapHighlightColor: "transparent" }}>
              <div style={{ fontSize: 26, marginBottom: 10 }}>{g.icon}</div>
              <div style={{ fontSize: "clamp(12px,3vw,16px)", fontWeight: 700, color: g.color, letterSpacing: "0.1em", marginBottom: 6 }}>{g.label}</div>
              <div style={{ fontSize: "clamp(10px,2.5vw,12px)", color: "#666", marginBottom: 14, lineHeight: 1.5 }}>{g.desc}</div>
              <div style={{ fontSize: 10, color: "#303030", borderTop: "1px solid #111", paddingTop: 8, lineHeight: 1.5 }}>{g.keys}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════
   SLITHER
══════════════════════════════════════════════════════ */
function Snake({ onBack }) {
  const canvasRef = useRef(null);
  const stateRef = useRef(null);
  const rafRef = useRef(null);
  const lastStepRef = useRef(0);
  const touchStartRef = useRef(null);
  const [display, setDisplay] = useState({ score: 0, phase: "idle" });

  const COLS = 25, ROWS = 20, CELL = 26;
  const W = COLS * CELL, H = ROWS * CELL;
  const SPEED = 115;

  const mkFood = (snake) => {
    const isFree = (c, r) => !snake.some(([x, y]) => x === c && y === r);
    let c = rndInt(0, COLS), r = rndInt(0, ROWS);
    while (!isFree(c, r)) { c = rndInt(0, COLS); r = rndInt(0, ROWS); }
    return [c, r];
  };

  const initState = () => {
    const snake = [[12, 10], [11, 10], [10, 10]];
    const s = { snake, dir: [1, 0], nextDir: [1, 0], food: mkFood(snake), score: 0, dead: false };
    window._snakeState = s;
    return s;
  };

  const rrect = (ctx, x, y, w, h, r) => {
    ctx.beginPath(); ctx.moveTo(x + r, y); ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r); ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h); ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r); ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y); ctx.closePath();
  };

  const draw = (s) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#04040a"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#0c0c18";
    for (let x = 0; x < COLS; x++) for (let y = 0; y < ROWS; y++) ctx.fillRect(x * CELL + CELL / 2 - 1, y * CELL + CELL / 2 - 1, 2, 2);
    if (!s) return;
    const [fx, fy] = s.food;
    const pulse = 0.82 + 0.18 * Math.sin(Date.now() / 300);
    ctx.save(); ctx.shadowColor = "#ff3355"; ctx.shadowBlur = 18 * pulse; ctx.fillStyle = "#ff3355";
    ctx.beginPath(); ctx.arc(fx * CELL + CELL / 2, fy * CELL + CELL / 2, (CELL / 2 - 4) * pulse, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = "rgba(255,255,255,0.4)"; ctx.beginPath(); ctx.arc(fx * CELL + CELL / 2 - 2, fy * CELL + CELL / 2 - 3, 3, 0, Math.PI * 2); ctx.fill(); ctx.restore();
    s.snake.forEach(([x, y], i) => {
      const t = i / Math.max(s.snake.length - 1, 1);
      const g = Math.round(255 - t * 130); const pad = 2 + t * 1.5;
      ctx.save(); if (i === 0) { ctx.shadowColor = "#00ff88"; ctx.shadowBlur = 14; }
      ctx.fillStyle = i === 0 ? "#00ff88" : `rgb(0,${g},80)`;
      rrect(ctx, x * CELL + pad, y * CELL + pad, CELL - pad * 2, CELL - pad * 2, 5); ctx.fill(); ctx.restore();
    });
    if (s.snake.length > 0) {
      const [hx, hy] = s.snake[0]; const [dx, dy] = s.dir;
      const cx = hx * CELL + CELL / 2, cy = hy * CELL + CELL / 2;
      [1, -1].forEach(side => {
        const ex = cx + dx * 5 + (-dy) * 4 * side, ey = cy + dy * 5 + dx * 4 * side;
        ctx.fillStyle = "#04040a"; ctx.beginPath(); ctx.arc(ex, ey, 3, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = "#fff"; ctx.beginPath(); ctx.arc(ex, ey, 1.5, 0, Math.PI * 2); ctx.fill();
      });
    }
  };

  const step = (ts) => {
    const s = stateRef.current;
    if (!s || s.dead) return;
    if (ts - lastStepRef.current >= SPEED) {
      lastStepRef.current = ts;
      s.dir = [...s.nextDir];
      const [hx, hy] = s.snake[0]; const [dx, dy] = s.dir;
      const nx = (hx + dx + COLS) % COLS, ny = (hy + dy + ROWS) % ROWS;
      if (s.snake.some(([x, y]) => x === nx && y === ny)) {
        s.dead = true; draw(s); setDisplay({ score: s.score, phase: "dead" }); return;
      }
      const ate = nx === s.food[0] && ny === s.food[1];
      s.snake = [[nx, ny], ...s.snake.slice(0, ate ? undefined : -1)];
      if (ate) { s.food = mkFood(s.snake); s.score += 10; setDisplay({ score: s.score, phase: "playing" }); }
    }
    draw(s);
    rafRef.current = requestAnimationFrame(step);
  };

  const start = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    stateRef.current = initState(); lastStepRef.current = 0;
    setDisplay({ score: 0, phase: "playing" });
    rafRef.current = requestAnimationFrame(step);
  };

  const setDir = (dx, dy) => {
    const s = stateRef.current; if (!s) return;
    const nd = [dx, dy]; if (nd[0] !== -s.dir[0] || nd[1] !== -s.dir[1]) s.nextDir = nd;
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    draw(null);
    const canvas = canvasRef.current;
    const onKey = (e) => {
      const map = { ArrowUp:[0,-1], ArrowDown:[0,1], ArrowLeft:[-1,0], ArrowRight:[1,0], w:[0,-1], s:[0,1], a:[-1,0], d:[1,0] };
      const nd = map[e.key]; if (nd) { e.preventDefault(); setDir(nd[0], nd[1]); }
    };
    const onTS = (e) => { touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }; e.preventDefault(); };
    const onTE = (e) => {
      if (!touchStartRef.current) return;
      const dx = e.changedTouches[0].clientX - touchStartRef.current.x;
      const dy = e.changedTouches[0].clientY - touchStartRef.current.y;
      touchStartRef.current = null;
      if (Math.abs(dx) < 15 && Math.abs(dy) < 15) return;
      if (Math.abs(dx) > Math.abs(dy)) setDir(dx > 0 ? 1 : -1, 0); else setDir(0, dy > 0 ? 1 : -1);
      e.preventDefault();
    };
    window.addEventListener("keydown", onKey);
    canvas.addEventListener("touchstart", onTS, { passive: false });
    canvas.addEventListener("touchend", onTE, { passive: false });
    return () => { window.removeEventListener("keydown", onKey); canvas.removeEventListener("touchstart", onTS); canvas.removeEventListener("touchend", onTE); if (rafRef.current) cancelAnimationFrame(rafRef.current); delete window._snakeState; };
  }, []);

  return (
    <GameShell title="SLITHER" color="#00ff88" score={display.score} onBack={onBack}>
      <div style={{ position: "relative", width: "100%" }}>
        <canvas ref={canvasRef} width={W} height={H} style={{ display: "block", width: "100%", maxWidth: W, height: "auto", borderRadius: 10, touchAction: "none" }} />
        {display.phase !== "playing" && <Overlay color="#00ff88" title={display.phase === "dead" ? "GAME OVER" : "SLITHER"} score={display.phase === "dead" ? display.score : null} hint={display.phase === "idle" ? "Swipe or use D-Pad · WASD / Arrows" : ""} onStart={start} />}
      </div>
      <DPad color="#00ff88" onDir={setDir} />
      <div style={{ fontSize: 10, color: "#252535", marginTop: 8, letterSpacing: "0.08em" }}>SWIPE · D-PAD · WASD / ARROWS</div>
    </GameShell>
  );
}

/* ══════════════════════════════════════════════════════
   ROCK BLASTER
══════════════════════════════════════════════════════ */
function Asteroids({ onBack }) {
  const canvasRef = useRef(null);
  const stateRef = useRef(null);
  const keysRef = useRef({});
  const tcRef = useRef({ left: false, right: false, thrust: false, shoot: false });
  const rafRef = useRef(null);
  const [display, setDisplay] = useState({ score: 0, lives: 3, phase: "idle" });

  const W = 700, H = 500;

  const mkAsteroid = (x, y, size) => {
    const spd = rnd(0.4, 1.8) * (4 - size) * 0.5, ang = rnd(0, Math.PI * 2);
    const n = 9 + rndInt(0, 4);
    const pts = Array.from({ length: n }, (_, i) => { const a = (i / n) * Math.PI * 2, r = size * 18 + rnd(-size * 6, size * 6); return [Math.cos(a) * r, Math.sin(a) * r]; });
    return { x, y, vx: Math.cos(ang) * spd, vy: Math.sin(ang) * spd, size, pts, rot: rnd(0, Math.PI * 2), rotSpd: rnd(-0.025, 0.025) };
  };

  const spawnWave = (level, sx, sy) => Array.from({ length: 4 + level }, () => {
    let x, y; do { x = rnd(0, W); y = rnd(0, H); } while (Math.hypot(x - sx, y - sy) < 130);
    return mkAsteroid(x, y, 3);
  });

  const mkShip = () => ({ x: W / 2, y: H / 2, vx: 0, vy: 0, angle: -Math.PI / 2, inv: 150 });
  const initState = () => ({ ship: mkShip(), bullets: [], asteroids: spawnWave(1, W / 2, H / 2), particles: [], score: 0, lives: 3, level: 1, shootCD: 0, phase: "playing" });

  const explode = (s, x, y, color, n = 10) => {
    for (let i = 0; i < n; i++) { const spd = rnd(0.5, 3), ang = rnd(0, Math.PI * 2); s.particles.push({ x, y, vx: Math.cos(ang) * spd, vy: Math.sin(ang) * spd, life: rndInt(20, 45), maxLife: 45, r: rnd(1, 3.5), color }); }
  };

  const draw = (s) => {
    const canvas = canvasRef.current; if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#04040a"; ctx.fillRect(0, 0, W, H);
    for (let i = 0; i < 100; i++) { ctx.fillStyle = `rgba(255,255,255,${0.1 + (i % 5) * 0.08})`; ctx.fillRect((i * 137.5) % W, (i * 97.3 + i * 13.7) % H, i % 4 === 0 ? 2 : 1, i % 4 === 0 ? 2 : 1); }
    if (!s) return;
    s.particles.forEach(p => { const a = p.life / p.maxLife; ctx.save(); ctx.globalAlpha = a * a; ctx.shadowColor = p.color; ctx.shadowBlur = 6; ctx.fillStyle = p.color; ctx.beginPath(); ctx.arc(p.x, p.y, p.r * a, 0, Math.PI * 2); ctx.fill(); ctx.restore(); });
    s.asteroids.forEach(a => { ctx.save(); ctx.translate(a.x, a.y); ctx.rotate(a.rot); ctx.strokeStyle = "#6677aa"; ctx.lineWidth = 1.5; ctx.shadowColor = "#4455aa"; ctx.shadowBlur = 6; ctx.beginPath(); a.pts.forEach(([px, py], i) => i === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py)); ctx.closePath(); ctx.stroke(); ctx.restore(); });
    s.bullets.forEach(b => { ctx.save(); ctx.shadowColor = "#4488ff"; ctx.shadowBlur = 14; ctx.fillStyle = "#88ccff"; ctx.beginPath(); ctx.arc(b.x, b.y, 3, 0, Math.PI * 2); ctx.fill(); ctx.restore(); });
    const ship = s.ship; const tc = tcRef.current;
    if (ship.inv % 6 < 4) {
      ctx.save(); ctx.translate(ship.x, ship.y); ctx.rotate(ship.angle); ctx.shadowColor = "#4488ff"; ctx.shadowBlur = 16; ctx.strokeStyle = "#4488ff"; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.moveTo(20, 0); ctx.lineTo(-11, -11); ctx.lineTo(-6, 0); ctx.lineTo(-11, 11); ctx.closePath(); ctx.stroke();
      if (keysRef.current["ArrowUp"] || keysRef.current["w"] || tc.thrust) { ctx.strokeStyle = `rgba(255,${rndInt(80, 180)},0,0.9)`; ctx.shadowColor = "#ff8800"; ctx.shadowBlur = 12; ctx.lineWidth = 2; ctx.beginPath(); ctx.moveTo(-6, -5); ctx.lineTo(-18 - rnd(0, 10), 0); ctx.lineTo(-6, 5); ctx.stroke(); }
      ctx.restore();
    }
    for (let i = 0; i < s.lives; i++) { ctx.save(); ctx.translate(22 + i * 26, H - 22); ctx.rotate(-Math.PI / 2); ctx.strokeStyle = "#4488ff66"; ctx.lineWidth = 1.5; ctx.beginPath(); ctx.moveTo(14, 0); ctx.lineTo(-9, -9); ctx.lineTo(-5, 0); ctx.lineTo(-9, 9); ctx.closePath(); ctx.stroke(); ctx.restore(); }
    ctx.fillStyle = "#2a2a4a"; ctx.font = "11px 'Courier New'"; ctx.textAlign = "right"; ctx.fillText(`LVL ${s.level}`, W - 12, H - 10);
  };

  const loop = () => {
    const s = stateRef.current;
    if (s && s.phase === "playing") {
      const keys = keysRef.current; const tc = tcRef.current; const ship = s.ship;
      if (keys["ArrowLeft"] || keys["a"] || tc.left) ship.angle -= 0.055;
      if (keys["ArrowRight"] || keys["d"] || tc.right) ship.angle += 0.055;
      if (keys["ArrowUp"] || keys["w"] || tc.thrust) { ship.vx += Math.cos(ship.angle) * 0.22; ship.vy += Math.sin(ship.angle) * 0.22; }
      ship.vx *= 0.985; ship.vy *= 0.985;
      const spd = Math.hypot(ship.vx, ship.vy); if (spd > 8) { ship.vx *= 8 / spd; ship.vy *= 8 / spd; }
      ship.x = (ship.x + ship.vx + W) % W; ship.y = (ship.y + ship.vy + H) % H;
      if (ship.inv > 0) ship.inv--;
      if ((keys[" "] || keys["Space"] || tc.shoot) && s.shootCD <= 0) {
        s.bullets.push({ x: ship.x + Math.cos(ship.angle) * 20, y: ship.y + Math.sin(ship.angle) * 20, vx: Math.cos(ship.angle) * 10 + ship.vx, vy: Math.sin(ship.angle) * 10 + ship.vy, life: 55 });
        s.shootCD = 11;
      }
      if (s.shootCD > 0) s.shootCD--;
      s.bullets = s.bullets.filter(b => { b.life--; b.x = (b.x + b.vx + W) % W; b.y = (b.y + b.vy + H) % H; return b.life > 0; });
      s.asteroids.forEach(a => { a.x = (a.x + a.vx + W) % W; a.y = (a.y + a.vy + H) % H; a.rot += a.rotSpd; });
      s.bullets = s.bullets.filter(b => {
        const idx = s.asteroids.findIndex(a => Math.hypot(a.x - b.x, a.y - b.y) < a.size * 17);
        if (idx !== -1) { const a = s.asteroids.splice(idx, 1)[0]; s.score += (4 - a.size) * 20; explode(s, a.x, a.y, "#7788cc", 10 + a.size * 3); if (a.size > 1) { s.asteroids.push(mkAsteroid(a.x + 15, a.y, a.size - 1)); s.asteroids.push(mkAsteroid(a.x - 15, a.y, a.size - 1)); } setDisplay(d => ({ ...d, score: s.score })); return false; }
        return true;
      });
      if (ship.inv === 0) {
        const hit = s.asteroids.some(a => Math.hypot(a.x - ship.x, a.y - ship.y) < a.size * 16 + 8);
        if (hit) { explode(s, ship.x, ship.y, "#4488ff", 18); s.lives--; setDisplay(d => ({ ...d, lives: s.lives })); if (s.lives <= 0) { s.phase = "dead"; setDisplay({ score: s.score, lives: 0, phase: "dead" }); } else { ship.x = W / 2; ship.y = H / 2; ship.vx = 0; ship.vy = 0; ship.angle = -Math.PI / 2; ship.inv = 180; } }
      }
      if (s.asteroids.length === 0) { s.level++; s.asteroids = spawnWave(s.level, ship.x, ship.y); }
      s.particles = s.particles.filter(p => { p.x += p.vx; p.y += p.vy; return --p.life > 0; });
    }
    draw(stateRef.current);
    rafRef.current = requestAnimationFrame(loop);
  };

  const start = () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); stateRef.current = initState(); setDisplay({ score: 0, lives: 3, phase: "playing" }); rafRef.current = requestAnimationFrame(loop); };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    draw(null); rafRef.current = requestAnimationFrame(loop);
    const onKey = (e) => { keysRef.current[e.key] = e.type === "keydown"; if ([" ", "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key)) e.preventDefault(); };
    window.addEventListener("keydown", onKey); window.addEventListener("keyup", onKey);
    return () => { window.removeEventListener("keydown", onKey); window.removeEventListener("keyup", onKey); if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, []);

  const tc = tcRef.current; const C = "#4488ff";
  return (
    <GameShell title="ROCK BLASTER" color={C} score={display.score} lives={display.lives} onBack={onBack}>
      <div style={{ position: "relative", width: "100%" }}>
        <canvas ref={canvasRef} width={W} height={H} style={{ display: "block", width: "100%", maxWidth: W, height: "auto", borderRadius: 10, touchAction: "none" }} />
        {display.phase !== "playing" && <Overlay color={C} title={display.phase === "dead" ? "GAME OVER" : "ROCK BLASTER"} score={display.phase === "dead" ? display.score : null} hint={display.phase === "idle" ? "Use on-screen buttons or Arrows + Space" : ""} onStart={start} />}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", width: "100%", maxWidth: W, marginTop: 14, padding: "0 8px", boxSizing: "border-box" }}>
        <div style={{ display: "flex", gap: 10 }}>
          <VBtn label="◄" color={C} size={64} onDown={() => { tc.left = true; }} onUp={() => { tc.left = false; }} />
          <VBtn label="►" color={C} size={64} onDown={() => { tc.right = true; }} onUp={() => { tc.right = false; }} />
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <VBtn label="▲" color={C} size={64} onDown={() => { tc.thrust = true; }} onUp={() => { tc.thrust = false; }} />
          <VBtn label="●" color="#ff4488" size={64} onDown={() => { tc.shoot = true; }} onUp={() => { tc.shoot = false; }} />
        </div>
      </div>
      <div style={{ fontSize: 10, color: "#1e1e30", marginTop: 8, letterSpacing: "0.08em" }}>◄ ► ROTATE · ▲ THRUST · ● FIRE</div>
    </GameShell>
  );
}

/* ══════════════════════════════════════════════════════
   FLUTTER
══════════════════════════════════════════════════════ */
function Flappy({ onBack }) {
  const canvasRef = useRef(null);
  const stateRef = useRef(null);
  const rafRef = useRef(null);
  const [display, setDisplay] = useState({ score: 0, phase: "idle" });
  const W = 420, H = 620;
  const GRAV = 0.48, FLAP_VEL = -9.5, PIPE_W = 64, GAP = 158, PIPE_SPD = 2.8;

  const initState = () => ({ bird: { x: 90, y: H / 2, vy: 0, angle: 0 }, pipes: [], score: 0, phase: "playing", frame: 0, groundOffset: 0 });

  const flapRef = useRef(null);
  flapRef.current = () => {
    const s = stateRef.current;
    if (!s) { start(); return; }
    if (s.phase === "dead") { start(); return; }
    if (s.phase === "idle") s.phase = "playing";
    s.bird.vy = FLAP_VEL;
  };

  const draw = (s) => {
    const canvas = canvasRef.current; if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const sky = ctx.createLinearGradient(0, 0, 0, H);
    sky.addColorStop(0, "#0a0a1e"); sky.addColorStop(0.75, "#0e1a2e"); sky.addColorStop(1, "#0a1a14");
    ctx.fillStyle = sky; ctx.fillRect(0, 0, W, H);
    for (let i = 0; i < 60; i++) { ctx.fillStyle = `rgba(255,255,255,${0.1 + (i % 4) * 0.1})`; ctx.fillRect((i * 141.4) % W, (i * 83.1) % (H * 0.7), 1.5, 1.5); }
    const groundH = 72;
    ctx.fillStyle = "#0a1a0a"; ctx.fillRect(0, H - groundH, W, groundH);
    ctx.fillStyle = "#1a3a1a"; ctx.fillRect(0, H - groundH, W, 10);
    if (!s) return;
    ctx.fillStyle = "#2a5a2a40";
    for (let i = 0; i < 15; i++) { const gx = ((i * 40 - s.groundOffset) % W + W) % W; ctx.fillRect(gx, H - groundH, 22, 10); }
    s.pipes.forEach(p => {
      const topH = p.gapY, botY = p.gapY + GAP, botH = H - groundH - botY;
      const mg = () => { const g = ctx.createLinearGradient(p.x, 0, p.x + PIPE_W, 0); g.addColorStop(0, "#14551e"); g.addColorStop(0.3, "#1e8a2a"); g.addColorStop(1, "#0f3e18"); return g; };
      ctx.fillStyle = mg(); ctx.fillRect(p.x, 0, PIPE_W, topH); ctx.fillRect(p.x, botY, PIPE_W, botH);
      ctx.fillStyle = "#22aa30"; ctx.fillRect(p.x - 5, topH - 26, PIPE_W + 10, 26); ctx.fillRect(p.x - 5, botY, PIPE_W + 10, 26);
      ctx.fillStyle = "rgba(255,255,255,0.07)"; ctx.fillRect(p.x + 7, 0, 10, topH - 26); ctx.fillRect(p.x + 7, botY + 26, 10, Math.max(0, botH - 26));
    });
    const bird = s.bird;
    ctx.save(); ctx.translate(bird.x, bird.y); ctx.rotate(bird.angle);
    ctx.fillStyle = "rgba(0,0,0,0.2)"; ctx.beginPath(); ctx.ellipse(2, 2, 18, 14, 0, 0, Math.PI * 2); ctx.fill();
    ctx.shadowColor = "#ffaa00"; ctx.shadowBlur = 20; ctx.fillStyle = "#ffcc00"; ctx.beginPath(); ctx.ellipse(0, 0, 18, 14, 0, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = "#ffe066"; ctx.beginPath(); ctx.ellipse(4, 3, 10, 8, 0.3, 0, Math.PI * 2); ctx.fill();
    ctx.shadowBlur = 0; ctx.fillStyle = "#ff9900"; ctx.beginPath(); ctx.ellipse(-3, Math.sin(s.frame * 0.35) * 6, 11, 6, -0.4, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = "#fff"; ctx.beginPath(); ctx.arc(8, -3, 5.5, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = "#1a1a1a"; ctx.beginPath(); ctx.arc(9.5, -3, 3, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = "#fff"; ctx.beginPath(); ctx.arc(10.5, -4.5, 1.2, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = "#ff7700"; ctx.beginPath(); ctx.moveTo(16, -1); ctx.lineTo(25, -4); ctx.lineTo(25, 1); ctx.lineTo(16, 3); ctx.closePath(); ctx.fill();
    ctx.restore();
    ctx.textAlign = "center"; ctx.font = "bold 40px 'Courier New'"; ctx.fillStyle = "rgba(255,255,255,0.85)"; ctx.shadowColor = "rgba(0,0,0,0.6)"; ctx.shadowBlur = 8;
    ctx.fillText(s.score, W / 2, 56); ctx.shadowBlur = 0;
  };

  const loop = () => {
    const s = stateRef.current;
    if (s && s.phase === "playing") {
      const bird = s.bird; s.frame++; s.groundOffset += PIPE_SPD;
      bird.vy += GRAV; bird.y += bird.vy;
      bird.angle = Math.max(-0.5, Math.min(1.3, bird.vy * 0.072));
      if (s.frame % 85 === 0) s.pipes.push({ x: W + 20, gapY: rndInt(70, H - 72 - GAP - 70), scored: false });
      s.pipes.forEach(p => p.x -= PIPE_SPD);
      s.pipes = s.pipes.filter(p => p.x > -PIPE_W - 20);
      s.pipes.forEach(p => { if (!p.scored && p.x + PIPE_W < bird.x) { p.scored = true; s.score++; setDisplay(d => ({ ...d, score: s.score })); } });
      const groundH = 72;
      if (bird.y + 14 >= H - groundH || bird.y - 14 <= 0) { s.phase = "dead"; setDisplay({ score: s.score, phase: "dead" }); }
      const hitPipe = s.pipes.some(p => { const inX = bird.x + 13 > p.x && bird.x - 13 < p.x + PIPE_W; return inX && (bird.y - 11 < p.gapY || bird.y + 11 > p.gapY + GAP); });
      if (hitPipe) { s.phase = "dead"; setDisplay({ score: s.score, phase: "dead" }); }
    }
    draw(stateRef.current);
    rafRef.current = requestAnimationFrame(loop);
  };

  const start = () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); stateRef.current = initState(); setDisplay({ score: 0, phase: "playing" }); rafRef.current = requestAnimationFrame(loop); };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    draw(null); rafRef.current = requestAnimationFrame(loop);
    const canvas = canvasRef.current;
    const onKey = (e) => { if (e.key === " ") { e.preventDefault(); flapRef.current(); } };
    const onTouch = (e) => { e.preventDefault(); flapRef.current(); };
    window.addEventListener("keydown", onKey);
    canvas.addEventListener("touchstart", onTouch, { passive: false });
    return () => { window.removeEventListener("keydown", onKey); canvas.removeEventListener("touchstart", onTouch); if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, []);

  return (
    <GameShell title="FLUTTER" color="#ffaa00" score={display.score} onBack={onBack}>
      <div style={{ position: "relative", width: "100%", display: "flex", justifyContent: "center" }}>
        <canvas ref={canvasRef} width={W} height={H} style={{ display: "block", width: "100%", maxWidth: W, height: "auto", borderRadius: 10, cursor: "pointer", touchAction: "none" }} onClick={() => flapRef.current()} />
        {display.phase !== "playing" && <Overlay color="#ffaa00" title={display.phase === "dead" ? "GAME OVER" : "FLUTTER"} score={display.phase === "dead" ? display.score : null} hint={display.phase === "idle" ? "Tap screen or press Space to flap" : "Tap to restart"} onStart={start} />}
      </div>
      <button onTouchStart={e => { e.preventDefault(); flapRef.current(); }} onClick={() => flapRef.current()} style={{ marginTop: 16, background: "#ffaa0015", border: "2px solid #ffaa0050", color: "#ffaa00", borderRadius: 16, padding: "18px 0", width: "100%", maxWidth: W, fontFamily: "'Courier New', monospace", fontSize: 18, fontWeight: 700, letterSpacing: "0.2em", cursor: "pointer", WebkitTapHighlightColor: "transparent", touchAction: "none" }}>▲ FLAP</button>
      <div style={{ fontSize: 10, color: "#1e1e20", marginTop: 8, letterSpacing: "0.08em" }}>TAP CANVAS · FLAP BUTTON · SPACE KEY</div>
    </GameShell>
  );
}

/* ══════════════════════════════════════════════════════
   NEON MAZE  — original maze-chomper, no Pac-Man assets
   Player = GOBBLER (cyan wedge). Enemies = WRAITHS (orbs).
   Maze, characters and names are wholly original.
══════════════════════════════════════════════════════ */
function Gobbler({ onBack }) {
  const canvasRef = useRef(null);
  const stateRef = useRef(null);
  const rafRef = useRef(null);
  const touchRef = useRef(null);
  const [disp, setDisp] = useState({ score: 0, lives: 3, phase: "idle" });

  // 19 cols × 21 rows, cell 26px
  const COLS = 19, ROWS = 21, CS = 26;
  const CW = COLS * CS, CH = ROWS * CS;
  const PP = 7;   // player: frames per tile
  const GP = 10;  // ghost: frames per tile

  // 0=dot 1=wall 2=power-pellet 3=ghost-zone(ghosts only) 4=tunnel(open,no dot)
  const BASE = [
    [1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1],
    [1,0,0,0,0,0,0,0,0,1,0,0,0,0,0,0,0,0,1],
    [1,2,1,1,0,1,1,1,0,1,0,1,1,1,0,1,1,2,1],
    [1,0,1,1,0,1,1,1,0,1,0,1,1,1,0,1,1,0,1],
    [1,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,1],
    [1,0,1,1,0,1,0,1,1,3,1,1,0,1,0,1,1,0,1],
    [1,0,1,1,0,1,0,0,0,3,0,0,0,1,0,1,1,0,1],
    [1,0,0,0,0,0,0,1,1,3,1,1,0,0,0,0,0,0,1],
    [1,1,1,1,0,1,0,1,3,3,3,1,0,1,0,1,1,1,1],
    [1,1,1,1,0,1,0,1,3,3,3,1,0,1,0,1,1,1,1],
    [4,4,4,4,0,0,0,1,3,3,3,1,0,0,0,4,4,4,4],
    [1,1,1,1,0,1,0,1,1,3,1,1,0,1,0,1,1,1,1],
    [1,1,1,1,0,1,0,0,0,3,0,0,0,1,0,1,1,1,1],
    [1,0,0,0,0,0,0,1,1,3,1,1,0,0,0,0,0,0,1],
    [1,0,1,1,0,1,0,0,0,0,0,0,0,1,0,1,1,0,1],
    [1,2,1,1,0,1,1,1,0,1,0,1,1,1,0,1,1,2,1],
    [1,0,0,0,0,0,0,0,0,1,0,0,0,0,0,0,0,0,1],
    [1,0,1,1,1,1,1,0,1,1,1,0,1,1,1,1,1,0,1],
    [1,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,1],
    [1,0,1,1,0,1,1,1,0,1,0,1,1,1,0,1,1,0,1],
    [1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1],
  ];

  const wc = c => ((c % COLS) + COLS) % COLS;

  // can the player pass this cell?
  const canP = (maze, c, r) => {
    if (r < 0 || r >= ROWS) return false;
    const v = maze[r][wc(c)];
    return v === 0 || v === 2 || v === 4;
  };

  // can a ghost pass this cell?
  const canG = (maze, c, r, exiting) => {
    if (r < 0 || r >= ROWS) return false;
    const v = maze[r][wc(c)];
    if (v === 1) return false;
    if (v === 3 && !exiting) return false;
    return true;
  };

  const mkMaze = () => BASE.map(r => [...r]);
  const countDots = (maze) => maze.flat().filter(v => v === 0 || v === 2).length;

  const GCOLORS = ["#ff4466", "#ff88ee", "#44ddff", "#ffaa22"];
  const GCRATE  = [1.0, 0.75, 0.5, 0.35];
  // ghost house spawn positions
  const GSPAWN  = [[9,9],[8,9],[10,9],[9,8]];

  const mkPlayer = () => ({
    col: 9, row: 18, pc: 9, pr: 18,
    dx: 0, dy: 0, ndx: 0, ndy: 0, timer: 0,
    lastDx: 1, lastDy: 0,
  });

  const mkGhost = (i) => ({
    col: GSPAWN[i][0], row: GSPAWN[i][1],
    pc:  GSPAWN[i][0], pr:  GSPAWN[i][1],
    dc: 0, dr: -1, timer: GP,
    exiting: true, scared: false, scaredT: 0,
    color: GCOLORS[i], cr: GCRATE[i], id: i,
    delay: i * 45,
  });

  const mkState = () => {
    const maze = mkMaze();
    return {
      maze, totalDots: countDots(maze), dotsEaten: 0,
      player: mkPlayer(),
      ghosts: [0,1,2,3].map(mkGhost),
      score: 0, lives: 3, phase: "playing",
      eatCombo: 0, frame: 0, inv: 0, levelFlash: 0,
    };
  };

  const DIRS4 = [[0,-1],[1,0],[0,1],[-1,0]];

  // Smooth pixel position between previous tile (pc,pr) and current (col,row)
  const getPos = (e, period) => {
    const t = e.timer / period;
    let dc = e.col - e.pc;
    if (dc >  COLS / 2) dc -= COLS;
    if (dc < -COLS / 2) dc += COLS;
    return {
      px: e.pc * CS + dc * CS * (1 - t),
      py: e.pr * CS + (e.row - e.pr) * CS * (1 - t),
    };
  };

  const moveGhost = (g, s) => {
    if (g.delay > 0) { g.delay--; return; }
    if (g.timer > 0) { g.timer--; return; }

    g.pc = g.col; g.pr = g.row;
    g.timer = g.scared ? GP + 5 : GP;

    // valid moves (no U-turn)
    let opts = DIRS4.map(([dc, dr]) => ({ dc, dr }))
      .filter(d => !(d.dc === -g.dc && d.dr === -g.dr))
      .filter(d => canG(s.maze, g.col + d.dc, g.row + d.dr, g.exiting));

    if (opts.length === 0) {
      // forced U-turn
      opts = DIRS4.map(([dc, dr]) => ({ dc, dr })).filter(d => canG(s.maze, g.col + d.dc, g.row + d.dr, g.exiting));
    }
    if (opts.length === 0) return;

    const p = s.player;
    let target;
    if (g.exiting)      target = { col: 9, row: 4 };
    else if (g.scared)  target = { col: (p.col + 9) % COLS, row: Math.min(ROWS - 2, p.row + 10) };
    else                target = { col: p.col, row: p.row };

    let chosen;
    if (g.exiting || Math.random() < g.cr) {
      opts.sort((a, b) => {
        const da = Math.hypot(wc(g.col + a.dc) - target.col, g.row + a.dr - target.row);
        const db = Math.hypot(wc(g.col + b.dc) - target.col, g.row + b.dr - target.row);
        return g.scared ? db - da : da - db;
      });
      chosen = opts[0];
    } else {
      chosen = opts[rndInt(0, opts.length)];
    }

    g.dc = chosen.dc; g.dr = chosen.dr;
    g.col = wc(g.col + g.dc); g.row = g.row + g.dr;

    if (g.exiting) {
      const v = s.maze[g.row]?.[g.col];
      if (v !== 3) g.exiting = false;
    }
  };

  const draw = (s) => {
    const canvas = canvasRef.current; if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#04040a"; ctx.fillRect(0, 0, CW, CH);
    if (!s) return;

    const f = s.frame;

    // Draw maze
    for (let r = 0; r < ROWS; r++) {
      for (let c = 0; c < COLS; c++) {
        const v = s.maze[r][c];
        const x = c * CS, y = r * CS;
        if (v === 1) {
          ctx.fillStyle = "#0c1e44"; ctx.fillRect(x, y, CS, CS);
          ctx.strokeStyle = "#1a3a88"; ctx.lineWidth = 1;
          // neon edge highlight
          const U = r > 0 && s.maze[r-1][c] !== 1;
          const D = r < ROWS-1 && s.maze[r+1][c] !== 1;
          const L = c > 0 && s.maze[r][c-1] !== 1;
          const R = c < COLS-1 && s.maze[r][c+1] !== 1;
          ctx.strokeStyle = "#2255bb";
          if (U) { ctx.beginPath(); ctx.moveTo(x, y + 0.5); ctx.lineTo(x + CS, y + 0.5); ctx.stroke(); }
          if (D) { ctx.beginPath(); ctx.moveTo(x, y + CS - 0.5); ctx.lineTo(x + CS, y + CS - 0.5); ctx.stroke(); }
          if (L) { ctx.beginPath(); ctx.moveTo(x + 0.5, y); ctx.lineTo(x + 0.5, y + CS); ctx.stroke(); }
          if (R) { ctx.beginPath(); ctx.moveTo(x + CS - 0.5, y); ctx.lineTo(x + CS - 0.5, y + CS); ctx.stroke(); }
        } else if (v === 0) {
          ctx.fillStyle = "#99bbff"; ctx.beginPath(); ctx.arc(x + CS/2, y + CS/2, 2.2, 0, Math.PI*2); ctx.fill();
        } else if (v === 2) {
          const pulse = 0.75 + 0.25 * Math.sin(f * 0.12);
          ctx.save(); ctx.shadowColor = "#ffcc00"; ctx.shadowBlur = 10;
          ctx.fillStyle = "#ffcc00"; ctx.beginPath(); ctx.arc(x + CS/2, y + CS/2, 5 * pulse, 0, Math.PI*2); ctx.fill();
          ctx.restore();
        } else if (v === 3) {
          ctx.fillStyle = "#070710"; ctx.fillRect(x, y, CS, CS);
        }
      }
    }

    // Ghost house door
    ctx.strokeStyle = "#ff44aa88"; ctx.lineWidth = 2; ctx.setLineDash([3, 3]);
    ctx.beginPath(); ctx.moveTo(8 * CS, 7 * CS); ctx.lineTo(11 * CS, 7 * CS); ctx.stroke();
    ctx.setLineDash([]);

    // Level flash overlay
    if (s.levelFlash > 0) {
      ctx.fillStyle = `rgba(0,255,200,${s.levelFlash / 60 * 0.3})`;
      ctx.fillRect(0, 0, CW, CH);
    }

    // Draw wraiths (enemies)
    s.ghosts.forEach(g => {
      const period = g.scared ? GP + 5 : GP;
      const { px, py } = getPos(g, period);
      const cx = px + CS/2, cy = py + CS/2, r2 = CS/2 - 2;
      const flash = g.scared && g.scaredT < 150 && Math.floor(f / 7) % 2 === 0;
      const col = flash ? "#ffffff" : g.scared ? "#2233bb" : g.color;

      ctx.save();
      ctx.shadowColor = col; ctx.shadowBlur = 8; ctx.fillStyle = col;

      // Wraith body: round top, jagged bottom (clearly NOT the Pac-Man ghost silhouette)
      ctx.beginPath();
      ctx.arc(cx, cy - 2, r2, Math.PI, 0);
      const bY = cy - 2 + r2;
      ctx.lineTo(cx + r2, bY);
      // 4-tooth sawtooth bottom — different from the smooth ghost skirt
      const teeth = 4;
      for (let i = teeth; i >= 0; i--) {
        const tx = cx - r2 + (i / teeth) * r2 * 2;
        const ty = bY - (i % 2 === 0 ? 5 : 0);
        ctx.lineTo(tx, ty);
      }
      ctx.closePath(); ctx.fill();

      // Eyes
      ctx.save(); ctx.shadowBlur = 0;
      if (!g.scared || flash) {
        const ew = 3, eh = 4;
        [-1, 1].forEach(side => {
          const ex = cx + side * r2 * 0.38, ey = cy - r2 * 0.2;
          ctx.fillStyle = "#ffffff"; ctx.beginPath(); ctx.ellipse(ex, ey, ew, eh, 0, 0, Math.PI*2); ctx.fill();
          ctx.fillStyle = g.scared ? "#aaaaff" : "#001188";
          ctx.beginPath(); ctx.ellipse(ex + g.dc * 1.5, ey + g.dr * 1.5, 1.5, 2, 0, 0, Math.PI*2); ctx.fill();
        });
      } else {
        ctx.fillStyle = "#aaaaff"; ctx.font = `${Math.round(CS * 0.38)}px Arial`;
        ctx.textAlign = "center"; ctx.textBaseline = "middle"; ctx.fillText("><", cx, cy - 3);
      }
      ctx.restore(); ctx.restore();
    });

    // Draw GOBBLER (player) — cyan wedge, clearly not the yellow Pac-Man
    if (s.inv === 0 || Math.floor(f / 5) % 2 === 0) {
      const p = s.player;
      const { px, py } = getPos(p, PP);
      const cx = px + CS/2, cy = py + CS/2, r2 = CS/2 - 2;

      const lDx = p.dx !== 0 ? p.dx : p.lastDx;
      const lDy = p.dy !== 0 ? p.dy : p.lastDy;
      let fAng = 0;
      if (lDx === 1) fAng = 0; else if (lDx === -1) fAng = Math.PI;
      else if (lDy === -1) fAng = -Math.PI/2; else fAng = Math.PI/2;

      const moving = p.dx !== 0 || p.dy !== 0;
      const ma = moving ? 0.12 + 0.22 * Math.abs(Math.sin(f * 0.28)) : 0.05;

      ctx.save();
      ctx.shadowColor = "#00ffcc"; ctx.shadowBlur = 14; ctx.fillStyle = "#00ffcc";
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.arc(cx, cy, r2, fAng + ma, fAng + Math.PI * 2 - ma);
      ctx.closePath(); ctx.fill();

      // Eye on gobbler
      const eyeX = cx + Math.cos(fAng - 0.7) * r2 * 0.55;
      const eyeY = cy + Math.sin(fAng - 0.7) * r2 * 0.55;
      ctx.fillStyle = "#003322"; ctx.shadowBlur = 0;
      ctx.beginPath(); ctx.arc(eyeX, eyeY, 2, 0, Math.PI*2); ctx.fill();
      ctx.restore();
    }

    // HUD: dots remaining
    ctx.fillStyle = "#1a2a2a"; ctx.font = "10px 'Courier New'"; ctx.textAlign = "left";
    ctx.fillText(`DOTS ${s.totalDots - s.dotsEaten}`, 4, CH - 4);
  };

  const loop = () => {
    const s = stateRef.current;
    if (s && s.phase === "playing") {
      s.frame++;
      if (s.inv > 0) s.inv--;
      if (s.levelFlash > 0) s.levelFlash--;

      // Scare timers
      s.ghosts.forEach(g => { if (g.scared && g.scaredT > 0) { g.scaredT--; if (!g.scaredT) g.scared = false; } });

      // Player movement
      const p = s.player;
      if (p.timer > 0) {
        p.timer--;
      } else {
        let moved = false;
        // Try queued direction
        if (p.ndx !== 0 || p.ndy !== 0) {
          const nc = wc(p.col + p.ndx), nr = p.row + p.ndy;
          if (canP(s.maze, nc, nr)) {
            p.pc = p.col; p.pr = p.row; p.col = nc; p.row = nr;
            p.dx = p.ndx; p.dy = p.ndy; p.timer = PP; moved = true;
          }
        }
        // Try current direction
        if (!moved && (p.dx !== 0 || p.dy !== 0)) {
          const nc = wc(p.col + p.dx), nr = p.row + p.dy;
          if (canP(s.maze, nc, nr)) {
            p.pc = p.col; p.pr = p.row; p.col = nc; p.row = nr; p.timer = PP; moved = true;
          } else {
            p.dx = 0; p.dy = 0;
          }
        }

        // Collect
        if (moved) {
          if (p.dx !== 0) p.lastDx = p.dx;
          if (p.dy !== 0) p.lastDy = p.dy;
          const v = s.maze[p.row][p.col];
          if (v === 0 || v === 2) {
            s.maze[p.row][p.col] = 4; // consumed — keep passable
            s.dotsEaten++;
            if (v === 0) s.score += 10;
            if (v === 2) {
              s.score += 50; s.eatCombo = 0;
              s.ghosts.forEach(g => { if (!g.exiting) { g.scared = true; g.scaredT = 600; } });
            }
            setDisp(d => ({ ...d, score: s.score }));
          }
        }
      }

      // Ghost movement
      s.ghosts.forEach(g => moveGhost(g, s));

      // Collision: player vs wraiths
      if (s.inv === 0) {
        for (const g of s.ghosts) {
          if (Math.abs(g.col - p.col) < 1.2 && Math.abs(g.row - p.row) < 1.2) {
            if (g.scared) {
              // eat wraith
              s.eatCombo++;
              const pts = 200 * Math.pow(2, s.eatCombo - 1);
              s.score += pts;
              setDisp(d => ({ ...d, score: s.score }));
              const sp = GSPAWN[g.id];
              g.col = sp[0]; g.row = sp[1]; g.pc = sp[0]; g.pr = sp[1];
              g.dc = 0; g.dr = -1; g.scared = false; g.scaredT = 0;
              g.exiting = true; g.delay = 30; g.timer = GP;
            } else {
              // player dies
              s.lives--; s.inv = 180; s.eatCombo = 0;
              setDisp(d => ({ ...d, lives: s.lives }));
              // reset player
              const np = mkPlayer();
              Object.assign(p, np);
              // reset wraiths
              s.ghosts.forEach((gg, i) => {
                const sp = GSPAWN[i];
                gg.col = sp[0]; gg.row = sp[1]; gg.pc = sp[0]; gg.pr = sp[1];
                gg.dc = 0; gg.dr = -1; gg.scared = false; gg.scaredT = 0;
                gg.exiting = true; gg.delay = i * 45; gg.timer = GP;
              });
              if (s.lives <= 0) { s.phase = "dead"; setDisp({ score: s.score, lives: 0, phase: "dead" }); }
              break;
            }
          }
        }
      }

      // Level complete
      if (s.phase === "playing" && s.dotsEaten >= s.totalDots) {
        s.phase = "levelup"; s.levelFlash = 90;
        setTimeout(() => {
          if (stateRef.current?.phase === "levelup") {
            const ns = mkState();
            ns.score = s.score; ns.lives = s.lives;
            stateRef.current = ns;
            setDisp({ score: ns.score, lives: ns.lives, phase: "playing" });
          }
        }, 1800);
      }
    }

    draw(stateRef.current);
    rafRef.current = requestAnimationFrame(loop);
  };

  const start = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    stateRef.current = mkState();
    setDisp({ score: 0, lives: 3, phase: "playing" });
    rafRef.current = requestAnimationFrame(loop);
  };

  const setDir = (dx, dy) => {
    const s = stateRef.current;
    if (!s || s.phase !== "playing") return;
    s.player.ndx = dx; s.player.ndy = dy;
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    draw(null);
    rafRef.current = requestAnimationFrame(loop);
    const canvas = canvasRef.current;

    const onKey = (e) => {
      const map = { ArrowUp:[0,-1], ArrowDown:[0,1], ArrowLeft:[-1,0], ArrowRight:[1,0], w:[0,-1], s:[0,1], a:[-1,0], d:[1,0] };
      const d = map[e.key]; if (d) { e.preventDefault(); setDir(d[0], d[1]); }
    };
    const onTS = (e) => { touchRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }; e.preventDefault(); };
    const onTE = (e) => {
      if (!touchRef.current) return;
      const dx = e.changedTouches[0].clientX - touchRef.current.x;
      const dy = e.changedTouches[0].clientY - touchRef.current.y;
      touchRef.current = null;
      if (Math.abs(dx) < 15 && Math.abs(dy) < 15) return;
      if (Math.abs(dx) > Math.abs(dy)) setDir(dx > 0 ? 1 : -1, 0); else setDir(0, dy > 0 ? 1 : -1);
      e.preventDefault();
    };

    window.addEventListener("keydown", onKey);
    canvas.addEventListener("touchstart", onTS, { passive: false });
    canvas.addEventListener("touchend", onTE, { passive: false });
    return () => {
      window.removeEventListener("keydown", onKey);
      canvas.removeEventListener("touchstart", onTS);
      canvas.removeEventListener("touchend", onTE);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const C = "#00ffcc";
  return (
    <GameShell title="NEON MAZE" color={C} score={disp.score} lives={disp.lives} onBack={onBack}>
      <div style={{ position: "relative", width: "100%", display: "flex", justifyContent: "center" }}>
        <canvas ref={canvasRef} width={CW} height={CH}
          style={{ display: "block", width: "100%", maxWidth: CW, height: "auto", borderRadius: 10, touchAction: "none" }} />
        {disp.phase !== "playing" && disp.phase !== "levelup" && (
          <Overlay color={C}
            title={disp.phase === "dead" ? "GAME OVER" : "NEON MAZE"}
            score={disp.phase === "dead" ? disp.score : null}
            hint={disp.phase === "idle" ? "Swipe or D-Pad · Eat all nodes · Power nodes stun Wraiths!" : undefined}
            onStart={start} />
        )}
      </div>
      <DPad color={C} onDir={setDir} />
      <div style={{ fontSize: 10, color: "#1a2a2a", marginTop: 8, letterSpacing: "0.08em" }}>SWIPE · D-PAD · WASD / ARROWS &nbsp;·&nbsp; POWER NODES STUN WRAITHS</div>
    </GameShell>
  );
}

/* ══════════════════════════════════════════════════════
   APP ROOT
══════════════════════════════════════════════════════ */
export default function App() {
  const [screen, setScreen] = useState("menu");
  if (screen === "snake")     return <Snake     onBack={() => setScreen("menu")} />;
  if (screen === "asteroids") return <Asteroids onBack={() => setScreen("menu")} />;
  if (screen === "flappy")    return <Flappy    onBack={() => setScreen("menu")} />;
  if (screen === "gobbler")   return <Gobbler   onBack={() => setScreen("menu")} />;
  return <Menu onSelect={setScreen} />;
}
